export type PaymentPortalKey = "card" | "cardExpiry" | "cardCvc" | "prButton";

const PORTAL_SLOTS: Record<PaymentPortalKey, string> = {
  card: "#zpay-card",
  cardExpiry: "#zpay-card-expiry",
  cardCvc: "#zpay-card-cvc",
  prButton: "#zpay-prbutton",
};

type PortalChain = {
  /** Document-rooted node Stripe mounts into, a light child of the top host. */
  mount: HTMLDivElement;
  /** `<slot>` inside the in-template placeholder. */
  inner: HTMLSlotElement;
  /** One pass-through `<slot>` per intermediate shadow host. */
  forwards: HTMLSlotElement[];
  /** Re-pins Stripe's iframes whenever Stripe inserts or restyles them. */
  observer: MutationObserver;
};

/**
 * Declarations held on every Stripe iframe with inline `!important`, which
 * outranks any host-page stylesheet rule.
 */
const IFRAME_PINS: Record<string, string> = {
  "background-color": "transparent",
  "background-image": "none",
  "box-shadow": "none",
  "min-height": "0px",
  "max-height": "none",
  "max-width": "none",
};

let nextControllerId = 0;

function isInShadowTree(node: Node): boolean {
  return node.getRootNode() instanceof ShadowRoot;
}

/**
 * The projected mount is a light-DOM descendant of the host page's own
 * containers, so page rules such as thirdspaceand.co's
 * `.booking-modal__embed iframe { height: …!important }` reach Stripe's
 * iframes and stretch each field to the page's chosen height. Stripe sets the
 * iframe's real height inline without `!important`; re-assert it (and reset
 * the box styling) as inline `!important` so page CSS cannot override it.
 */
function pinStripeFrames(mount: HTMLElement): void {
  for (const frame of mount.querySelectorAll("iframe")) {
    const { style } = frame;
    const height = style.getPropertyValue("height");
    if (height && style.getPropertyPriority("height") !== "important") {
      style.setProperty("height", height, "important");
    }
    for (const [property, value] of Object.entries(IFRAME_PINS)) {
      if (style.getPropertyPriority(property) !== "important") {
        style.setProperty(property, value, "important");
      }
    }
  }
}

function namedSlot(name: string): HTMLSlotElement {
  const slot = document.createElement("slot");
  slot.name = name;
  return slot;
}

/**
 * Owns the light-DOM mount targets required by Stripe Elements when the
 * payment UI itself is nested under another component's shadow root.
 *
 * Stripe refuses shadow-rooted mount nodes, so the mount node lives in the
 * document, as a light child of the outermost shadow host, and is projected
 * back into the in-template placeholder through a chain of named slots (one
 * pass-through slot per intermediate shadow host). The browser lays the
 * iframe out in normal flow at the placeholder: scrolling, the iOS keyboard,
 * transforms and ancestor clipping all behave natively.
 *
 * PLAT-7133: the previous approach overlaid a `position: fixed` div on the
 * placeholder's rect each frame. iOS Safari moves the fixed layer and the
 * page independently around the on-screen keyboard, so the iframes drifted
 * away from their boxes and typing landed in the wrong, invisible field.
 */
export class PaymentPortalController {
  private readonly portals: Record<PaymentPortalKey, PortalChain | null> = {
    card: null,
    cardExpiry: null,
    cardCvc: null,
    prButton: null,
  };

  private readonly id = ++nextControllerId;

  constructor(private readonly host: HTMLElement) {}

  resolveMountTarget(slot: HTMLElement, which: PaymentPortalKey): HTMLElement {
    if (!isInShadowTree(slot)) return slot;
    const portal = this.portals[which] ?? this.createChain(slot, which);
    this.attachInner(portal, slot);
    return portal.mount;
  }

  /**
   * Re-seat each inner slot after a re-render replaced its placeholder. A
   * missing placeholder needs nothing: an unassigned mount node is not
   * rendered, so the field disappears with its step.
   */
  sync(): void {
    for (const which of Object.keys(this.portals) as PaymentPortalKey[]) {
      const portal = this.portals[which];
      if (!portal) continue;
      const slot = this.host.querySelector<HTMLElement>(PORTAL_SLOTS[which]);
      if (slot) this.attachInner(portal, slot);
    }
  }

  destroy(which: PaymentPortalKey): void {
    const portal = this.portals[which];
    if (!portal) return;
    portal.observer.disconnect();
    portal.mount.remove();
    portal.inner.remove();
    for (const forward of portal.forwards) forward.remove();
    this.portals[which] = null;
  }

  teardown(): void {
    for (const which of Object.keys(this.portals) as PaymentPortalKey[]) {
      this.destroy(which);
    }
  }

  private slotName(which: PaymentPortalKey): string {
    return `zpay-${which}-${this.id}`;
  }

  private createChain(slot: HTMLElement, which: PaymentPortalKey): PortalChain {
    const name = this.slotName(which);
    const forwards: HTMLSlotElement[] = [];
    let host = (slot.getRootNode() as ShadowRoot).host;
    for (
      let root = host.getRootNode();
      root instanceof ShadowRoot;
      root = host.getRootNode()
    ) {
      const forward = namedSlot(name);
      forward.slot = name;
      // Prepend: a Lit child part that ends the host's children clears every
      // trailing sibling on re-render.
      host.prepend(forward);
      forwards.push(forward);
      host = root.host;
    }
    const mount = document.createElement("div");
    mount.slot = name;
    mount.dataset.zpayPortal = which;
    mount.style.display = "block";
    host.prepend(mount);
    const observer = new MutationObserver(() => pinStripeFrames(mount));
    observer.observe(mount, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });
    const portal = { mount, inner: namedSlot(name), forwards, observer };
    this.portals[which] = portal;
    return portal;
  }

  private attachInner(portal: PortalChain, slot: HTMLElement): void {
    if (portal.inner.parentElement !== slot) slot.append(portal.inner);
  }
}
